// Shared data + constants for the Huron Truss site
import {
  Hammer,
  Layers,
  Ruler,
  Truck,
  Compass,
  ShieldCheck,
  TreePine,
  Building2,
  Wrench,
} from "lucide-react";

export const COMPANY = {
  name: "Huron Truss Ltd.",
  tagline: "Quality. Service. Experience.",
  established: "2021",
  email: "paul@hurontruss.com",
  phone: "1-855-598-7877",
  phoneHref: "tel:+18555987877",
  logoDark: "/brand/huron-logo-light-bg.svg",
  logoLight: "/brand/huron-logo-dark-bg.svg",
  plants: [
    { name: "Bruce Mines", region: "Algoma District" },
    { name: "Val Gagné", region: "Cochrane District" },
  ],
  serviceAreas: [
    "Algoma District",
    "Sudbury District",
    "Nipissing District",
    "Manitoulin Island",
    "Cochrane District",
    "Timiskaming District",
    "Parry Sound District",
  ],
  team: [
    { name: "Paul DiVincenzo", role: "General Manager / Sales and Design", email: "paul@hurontruss.com" },
    { name: "Dan Legris", role: "Sales and Design", email: "dan@hurontruss.com" },
  ],
};

export const IMAGES = {
  hero: "https://static.prod-images.emergentagent.com/jobs/34a6986b-1dda-4dba-8fda-9ff70f93e44d/images/e214af0acee07b56f7effe08822d1258d3ae5d593c79970012383a459eb99683.png",
  glulam: "/products/glulam-beam.jpg",
  topoMap: "https://static.prod-images.emergentagent.com/jobs/34a6986b-1dda-4dba-8fda-9ff70f93e44d/images/880f64e07a57a3eaee8aca5c9cd4f670e60e1c391e934ff6895f8e2c2f69d7c0.png",
  p3Joists: "/products/p3-joists.jpg",
  roseburgLvl: "/products/roseburg-lvl.jpg",
  floorTruss: "/products/floor-truss-17ft.png",
  framing1: "https://images.unsplash.com/photo-1516880711640-ef7db81be3e1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxyb29mJTIwdHJ1c3MlMjBjb25zdHJ1Y3Rpb24lMjBzaXRlJTIwZnJhbWluZ3xlbnwwfHx8fDE3Nzk1ODQ3MzZ8MA&ixlib=rb-4.1.0&q=85",
  framing2: "https://images.unsplash.com/photo-1621673610286-a6b5e788ab82?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwzfHxyb29mJTIwdHJ1c3MlMjBjb25zdHJ1Y3Rpb24lMjBzaXRlJTIwZnJhbWluZ3xlbnwwfHx8fDE3Nzk1ODQ3MzZ8MA&ixlib=rb-4.1.0&q=85",
  framing3: "https://images.unsplash.com/photo-1556156657-b2925b706f19?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHw0fHxyb29mJTIwdHJ1c3MlMjBjb25zdHJ1Y3Rpb24lMjBzaXRlJTIwZnJhbWluZ3xlbnwwfHx8fDE3Nzk1ODQ3MzZ8MA&ixlib=rb-4.1.0&q=85",
  lumberYard: "https://images.pexels.com/photos/12278586/pexels-photo-12278586.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

export const PRODUCTS = [
  {
    slug: "roof-trusses",
    title: "Roof Trusses",
    summary: "Engineered roof trusses up to 80 feet, designed for residential, agricultural and commercial spans.",
    bullets: [
      "Clear spans up to 80 ft",
      "Common, scissor, attic & specialty profiles",
      "Simpson Strong-Tie connector plates",
      "Stamped engineered drawings",
    ],
    icon: Hammer,
  },
  {
    slug: "floor-trusses",
    title: "Floor Trusses",
    summary: "Open-web floor trusses that simplify MEP runs and deliver stiffer, quieter floors than dimensional lumber.",
    bullets: [
      "Long open spans, fewer support walls",
      "Easy plumbing & ductwork routing",
      "Clear spans up to 38 ft",
      "Depths up to 24\"",
    ],
    icon: Layers,
  },
  {
    slug: "i-joists",
    title: "I-Joists",
    summary: "Wood I-joists for floor and roof framing — straight, light, and dimensionally stable.",
    bullets: [
      "Consistent depths up to 24\"",
      "Reduced floor squeaks & shrinkage",
      "Engineered specific for your job",
      "Compatible LSL rim board supplied",
    ],
    icon: Ruler,
  },
  {
    slug: "lvl-lsl",
    title: "LVL & LSL",
    summary: "Laminated veneer & strand lumber for beams, headers, rim board, and column applications.",
    bullets: [
      "Higher load capacity than solid lumber",
      "Dimensionally stable & straight",
      "Custom-cut to project length",
      "Multi-ply assemblies",
    ],
    icon: Building2,
  },
  {
    slug: "glulam",
    title: "Glulam Beams",
    summary: "Glue-laminated timber beams for long-span, architectural and heavy-load applications.",
    bullets: [
      "Custom depths, widths and lengths",
      "Standard maximum depths up to 48\"",
      "Architectural appearance grades",
      "Pre-finished options available",
    ],
    icon: TreePine,
  },
  {
    slug: "columns-connectors",
    title: "Columns & Connectors",
    summary: "Engineered wood columns and a full inventory of hangers, post bases and structural connectors.",
    bullets: [
      "PSL & LVL columns to length",
      "Simpson Strong-Tie inventory",
      "Hangers, post bases, hold-downs",
    ],
    icon: Wrench,
  },
];

export const CAPABILITIES = [
  {
    icon: Compass,
    title: "In-House Design",
    body: "Our designers work directly from your prints or sketches to produce stamped engineered drawings for permit and framing.",
  },
  {
    icon: Hammer,
    title: "Local Manufacturing",
    body: "Two Northern Ontario plants — Bruce Mines and Val Gagné — running calibrated press lines side-by-side. Quality you can stand behind, capacity you can count on.",
  },
  {
    icon: Truck,
    title: "Delivery & Logistics",
    body: "Coordinated delivery on roll-off trailers in a range of lengths — drop the trusses on-site, our experienced drivers will get it there.",
  },
  {
    icon: ShieldCheck,
    title: "Personalized Service",
    body: "Direct lines to Paul and Dan from quote through delivery.",
  },
];

export const STATS = [
  { value: "80", label: "Feet — max clear truss span" },
  { value: "2", label: "Manufacturing plants — Bruce Mines & Val Gagné" },
  { value: "100+", label: "Years of combined staff experience" },
  { value: "100%", label: "Engineered & stamped designs" },
];

export const GALLERY = [
  { url: IMAGES.framing1, caption: "Residential roof truss installation", tall: true },
  { url: IMAGES.framing2, caption: "Stick-frame with engineered components" },
  { url: IMAGES.lumberYard, caption: "Engineered lumber inventory" },
  { url: IMAGES.framing3, caption: "Mid-span floor truss framing" },
  { url: IMAGES.glulam, caption: "Glulam beam — architectural finish" },
  { url: IMAGES.hero, caption: "Cathedral-style truss assembly", tall: true },
];

export const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/products", label: "Products" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];
